import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BrandingService } from './branding.service';

export interface SimulatorStep {
  step: number;
  route: string;
  name: string;
  hasValidation: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly steps: SimulatorStep[] = [
    { step: 0, route: '', name: 'Inicio', hasValidation: false },
    {
      step: 1,
      route: 'step-one',
      name: 'Datos Personales',
      hasValidation: true,
    },
    {
      step: 2,
      route: 'step-two',
      name: 'Información Financiera',
      hasValidation: true,
    },
    {
      step: 3,
      route: 'step-three',
      name: 'Términos y Condiciones',
      hasValidation: true,
    },
    { step: 4, route: 'step-four', name: 'Resultados', hasValidation: false },
    {
      step: 5,
      route: 'step-four-contact',
      name: 'Contacto',
      hasValidation: false,
    },
  ];

  constructor(
    private router: Router,
    private brandingService: BrandingService
  ) {}

  /**
   * Navega al paso anterior sin validaciones
   */
  goToPreviousStep(): void {
    const currentStep = this.getCurrentStepNumber();

    if (currentStep > 0) {
      const previousStep = this.steps[currentStep - 1];
      this.navigateToRoute(previousStep.route);
    }
  }

  /**
   * Navega al siguiente paso solo si es válido
   * @param isValid - true si el formulario actual es válido
   */
  goToNextStep(isValid: boolean = true): void {
    if (!isValid) {
      return;
    }

    const currentStep = this.getCurrentStepNumber();

    if (currentStep < this.steps.length - 1) {
      const nextStep = this.steps[currentStep + 1];
      this.navigateToRoute(nextStep.route);
    }
  }

  /**
   * Navega a un paso específico
   * @param stepNumber - número del paso (0-based)
   */
  goToStep(stepNumber: number): void {
    if (stepNumber >= 0 && stepNumber < this.steps.length) {
      const targetStep = this.steps[stepNumber];
      this.navigateToRoute(targetStep.route);
    }
  }

  /**
   * Obtiene el número del paso actual basado en la URL
   */
  getCurrentStepNumber(): number {
    const currentUrl = this.brandingService.normalizeUrl(this.router.url);
    const currentStep = this.steps.find(
      (step) =>
        currentUrl === `/${step.route}` ||
        (step.route === '' && currentUrl === '/')
    );

    return currentStep ? currentStep.step : 0;
  }

  /**
   * Verifica si se puede navegar hacia atrás
   */
  canGoBack(): boolean {
    return this.getCurrentStepNumber() > 0;
  }

  /**
   * Verifica si se puede navegar hacia adelante
   * @param isCurrentStepValid - si el paso actual es válido
   */
  canGoForward(isCurrentStepValid: boolean): boolean {
    const currentStep = this.getCurrentStepNumber();
    const isLastStep = currentStep >= this.steps.length - 1;

    return !isLastStep && isCurrentStepValid;
  }

  /**
   * Obtiene información del paso actual
   */
  getCurrentStep(): SimulatorStep | undefined {
    const stepNumber = this.getCurrentStepNumber();
    return this.steps[stepNumber];
  }

  /**
   * Obtiene información de todos los pasos
   */
  getAllSteps(): SimulatorStep[] {
    return [...this.steps];
  }

  /**
   * Obtiene la ruta de un paso específico
   */
  getStepRoute(stepNumber: number): string {
    const step = this.steps[stepNumber];
    return step ? step.route : '';
  }

  navigateToHome(): void {
    this.router.navigate(this.brandingService.withBrandPrefix([]));
  }

  navigateToError(): void {
    this.navigateToRoute('error404');
  }

  navigateToRoute(route: string): void {
    this.router.navigate(this.brandingService.withBrandPrefix([route]));
  }
}
